import { Link, NavLink } from "react-router-dom";
import { Menu, X, Heart, LogIn, LogOut, User, ShoppingBag } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { SHOPIFY_URL } from "@/constants/config";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/adopt", label: "Adopt" },
  { to: "/stories", label: "Stories" },
  { to: "/sponsor", label: "Sponsor" },
  { to: "/get-involved", label: "Get Involved" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, login, logout, favorites } = useAuth();
  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl border-b"
      style={{ backgroundColor: "rgba(250, 250, 247, 0.85)", borderColor: "#EADCC5" }}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-24">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_bulldog-sanctuary/artifacts/g4vq3c1t_1782224774424.png"
            alt="Lavish 808 Bulliez Rescued Paws Foundation"
            className="w-16 h-16 md:w-20 md:h-20 object-contain drop-shadow-sm"
          />
          <div className="hidden sm:block leading-tight">
            <div className="font-serif text-lg" style={{ color: "#0E5C6E" }}>
              Lavish 808 Bulliez
            </div>
            <div className="text-[0.65rem] tracking-[0.25em] uppercase" style={{ color: "#E27E4E" }}>
              Rescued Paws • Saving Since 2024
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-5">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) => `nav-link whitespace-nowrap ${isActive ? "active" : ""}`}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={SHOPIFY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="nav-link flex items-center gap-1.5"
            data-testid="nav-shop-link"
          >
            <ShoppingBag size={14} /> Shop
          </a>
          {user ? (
            <div className="flex items-center gap-3">
              <NavLink to="/favorites" className="nav-link relative flex items-center gap-1.5" data-testid="nav-favorites">
                <Heart size={14} fill={favorites.length ? "#E27E4E" : "none"} color={favorites.length ? "#E27E4E" : "currentColor"} />
                Favorites {favorites.length > 0 && <span className="text-[10px]">({favorites.length})</span>}
              </NavLink>
              <button onClick={logout} title="Sign out" className="text-xs uppercase tracking-[0.14em] flex items-center gap-1.5" style={{ color: "#0E5C6E" }} data-testid="nav-logout">
                {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full" /> : <User size={14} />}
                <LogOut size={12} />
              </button>
            </div>
          ) : (
            <button onClick={login} className="nav-link flex items-center gap-1.5" data-testid="nav-login">
              <LogIn size={14} /> Sign in
            </button>
          )}
          <NavLink
            to="/donate"
            className="btn-donate"
            data-testid="nav-donate-button"
          >
            <Heart size={16} fill="white" /> Donate
          </NavLink>
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t" style={{ borderColor: "#EADCC5" }}>
          <div className="px-4 py-4 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink
              to="/donate"
              onClick={() => setOpen(false)}
              className="btn-donate w-fit"
              data-testid="mobile-nav-donate-button"
            >
              <Heart size={16} fill="white" /> Donate
            </NavLink>
            <a
              href={SHOPIFY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="nav-link flex items-center gap-1.5"
              data-testid="mobile-nav-shop-link"
            >
              <ShoppingBag size={14} /> Shop
            </a>
            {user ? (
              <>
                <NavLink to="/favorites" onClick={() => setOpen(false)} className="nav-link">Favorites ({favorites.length})</NavLink>
                <button onClick={() => { logout(); setOpen(false); }} className="nav-link text-left" data-testid="mobile-nav-logout">Sign out</button>
              </>
            ) : (
              <button onClick={login} className="nav-link text-left" data-testid="mobile-nav-login">Sign in with Google</button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
